import { compactFlatten, object } from '@art-suite/art-core-ts';

import { OptimizedValueExpressionsBase, Scope } from '../OptimizedValueExpressions';
import {
  getBooleanScanValueOrUndefined,
  getIdsByDepth,
  SCAN_CHILDREN_BY_ID_FIELD_NAMES_BY_DEPTH,
  SCAN_CHILDREN_FIELD_NAMES_BY_DEPTH,
  SCAN_PART_DEPTH,
  SCAN_PART_TYPES_BY_DEPTH,
  ScanAudio,
  ScanGroup,
  ScanLineItem,
  ScanOption,
  ScanPart,
  ScanPhoto,
  ScanRoot,
  ScanTask,
  ScanValues,
  ScanVideo,
} from '../ScanRoot';
import {
  getChildTemplate,
  isExpressionValue,
  ScanTemplateGroup,
  ScanTemplateLineItem,
  ScanTemplateOption,
  ScanTemplatePart,
  ScanTemplateRoot,
  ScanTemplateTask,
  ScanTemplateValues,
} from '../ScanTemplateRoot';

import { pureFunctionalMap } from '../pureFunctionalLib';
import { getOptimizedValueExpressions } from './getOptimizedValueExpressions';
import {
  getAllAudio,
  getAllAudioIdsFromAllAudio,
  getAllAudioIdUsesByAudioId,
  getAllPhotoIdsFromAllPhotos,
  getAllPhotoIdUsesByPhotoId,
  getAllPhotos,
  getAllVideoIdsFromAllVideos,
  getAllVideos,
  getOptimizedSearchText,
  getReducedValues,
} from './ScanOptimizedAggregation';
import { isScanPartOptimized } from './ScanOptimizedGetters';
import { getScanPartStatus } from './ScanOptimizedStatus';
import {
  AudioIdUse,
  PhotoIdUse,
  ReducedValues,
  ScanGroupOptimized,
  ScanLineItemOptimized,
  ScanOptionOptimized,
  ScanPartOptimized,
  ScanRootOptimized,
  ScanTaskOptimized,
} from './ScanRootOptimizedSchemas';

const EMPTY_REDUCED_VALUES: ReducedValues = {};
const EMPTY_PHOTO_IDS: string[] = [];
const EMPTY_PHOTO_ID_USES_BY_PHOTO_ID: Record<string, PhotoIdUse[]> = {};
const EMPTY_AUDIO_ID_USES_BY_AUDIO_ID: Record<string, AudioIdUse[]> = {};
const EMPTY_VALUES: ScanValues = {};
const EMPTY_TEMPLATE_VALUES: ScanTemplateValues = {};
const EMPTY_SCOPE: Scope = {};
const EMPTY_PHOTOS: ScanPhoto[] = [];
const EMPTY_VIDEO_IDS: string[] = [];
const EMPTY_VIDEOS: ScanVideo[] = [];
const EMPTY_AUDIO_IDS: string[] = [];
const EMPTY_AUDIO: ScanAudio[] = [];

const getUpdatedScanValues = (
  templateValues: ScanTemplateValues,
  instanceValues: ScanValues,
  optimizedValueExpressionsWithCurrentParentScope: OptimizedValueExpressionsBase,
): ScanValues => {
  const values: ScanValues = {};
  let hasChanged = false;

  // never source calculatedValue from .resolved: resolved holds effective values, where setValue wins
  const calculatedValues = optimizedValueExpressionsWithCurrentParentScope.calculatedValues;

  // Initialize all template values, including text fields
  for (const key in templateValues) {
    const templateValue = templateValues[key];
    let calculatedValue = isExpressionValue(templateValue) ? undefined : templateValue.value;
    if (calculatedValue == null) calculatedValue = calculatedValues[key];
    // checkboxes are stored as booleans; the calculation engine computes them as 1/0
    if (templateValue.type === 'checkbox') calculatedValue = getBooleanScanValueOrUndefined(calculatedValue);
    const setValue = instanceValues[key]?.setValue ?? undefined;
    if (calculatedValue !== instanceValues[key]?.calculatedValue) {
      hasChanged = true;
    }
    values[key] = {
      calculatedValue,
      setValue,
    };
  }

  return hasChanged ? values : instanceValues;
};
// **************************************************************
// Get Optimized Scan Part
// **************************************************************

/**
 * The main, recursive function to convert a scan part to an optimized scan part
 *
 * We always assume scanPart is either not optimized, OR, if optimized:
 * 1. some of its props have changed
 * 2. NONE of its children have changed
 * @param scanTemplateRoot - can only be undefined if scanPart is optimized (is
 * needed for Groups to get the templates for Tasks in normalized templates)
 * @param template - the template for this scanPart
 * @param scanPart - the current value of the scanPart, possibly unoptimized
 * @param depth - 0 == scan, 1 == group, 2 == task, 3 == option, 4 == lineItem
 * @param idsByDepth - [scanId, groupId, taskId, optionId, lineItemId] (min length === 1)
 * @param parentScope - for calculations, the parent variable scope
 * @param optimizedScanPartBefore - if the scanPart was altered, then the input
 * `scanPart` should be the updated version, optimizedScanPartBefore should be
 * the scanPart's value BEFORE the alteration - provided for optimal re-use if
 * possible to avoid recreating objects unnecessarily
 * @returns
 */
export const getOptimizedScanPart = (
  scanTemplateRoot: ScanTemplateRoot | undefined,
  template: ScanTemplatePart,
  scanPart: ScanPart,
  depth: number,
  idsByDepth: (string | undefined)[],
  parentScope: Scope = EMPTY_SCOPE,
  optimizedScanPartBefore?: ScanPartOptimized,
): ScanPartOptimized => {
  const childrenArrayFieldName = SCAN_CHILDREN_FIELD_NAMES_BY_DEPTH[depth];
  const childrenByIdFieldName = SCAN_CHILDREN_BY_ID_FIELD_NAMES_BY_DEPTH[depth];
  const scanPartType = SCAN_PART_TYPES_BY_DEPTH[depth];
  if (!template) throw new Error(`Template is undefined for scan part ${scanPart.id} of type ${scanPartType}`);

  const scanPartIsOptimized = isScanPartOptimized(scanPart);
  const scanPartOptimizedChildrenBefore: ScanPartOptimized[] | undefined = optimizedScanPartBefore
    ? (optimizedScanPartBefore as any)[childrenArrayFieldName ?? '']
    : undefined;
  const scanPartChildren = (scanPart as any)[childrenArrayFieldName ?? ''] as ScanPart[] | undefined;

  const scanPartOptimizedChildren = scanPartIsOptimized ? scanPartChildren as ScanPartOptimized[] : undefined;

  // resolve values and scope for children
  const optimizedValueExpressions = getOptimizedValueExpressions(template, parentScope, scanPart, optimizedScanPartBefore?.optimized.optimizedValueExpressions);

  const values = getUpdatedScanValues(
    template.values ?? EMPTY_TEMPLATE_VALUES,
    scanPart.values ?? EMPTY_VALUES,
    optimizedValueExpressions,
  );
  const valuesChanged = values !== scanPart.values;
  // a new optimizedValueExpressions instance means the scope or setValues changed, which can change children even when this part's own values didn't (e.g. a setValue with no calculation of its own)
  const childScopeChanged = optimizedValueExpressions !== optimizedScanPartBefore?.optimized.optimizedValueExpressions;

  // recurse on children; if none change, use originalOptimizedChildren if available
  let pureFunctionalUpdatedChildren: ScanPartOptimized[] | undefined = scanPartOptimizedChildren;
  let updatedAt = scanPart.updatedAt;
  if (scanPartChildren && (!optimizedScanPartBefore || valuesChanged || childScopeChanged)) { // only recurse if the values or child scope changed, or if the children aren't optimized
    const scopeForChildren = optimizedValueExpressions.resolved;
    pureFunctionalUpdatedChildren = pureFunctionalMap(
      scanPartChildren as ScanPartOptimized[], // if the children aren't optimized, getOptimizedScanPart is guaranteed to return a new, optimized child
      child => {
        idsByDepth[depth + 1] = child.id;
        return getOptimizedScanPart(
          scanTemplateRoot,
          child.template ?? getChildTemplate(scanTemplateRoot, template, depth, child.templateId),
          child,
          depth + 1,
          idsByDepth,
          scopeForChildren,
        );
      },
    );
    if (pureFunctionalUpdatedChildren !== scanPartChildren) {
      updatedAt = Math.max(...compactFlatten([0, updatedAt, pureFunctionalUpdatedChildren?.map(child => child.updatedAt)]));
    }
    idsByDepth[depth + 1] = undefined;
  }

  // create the optimized scan part
  const ret: ScanPartOptimized = {
    ...scanPart,
    values,
    scanStatus: 'notStarted',
    template,
    scanPartType,
    updatedAt,

    optimized: {
      searchText: '',
      optimizedValueExpressions,
      reducedValues: EMPTY_REDUCED_VALUES,
      allPhotoIds: EMPTY_PHOTO_IDS,
      allPhotos: EMPTY_PHOTOS,
      allPhotoIdUsesByPhotoId: EMPTY_PHOTO_ID_USES_BY_PHOTO_ID,
      allVideoIds: EMPTY_VIDEO_IDS,
      allVideos: EMPTY_VIDEOS,
      allAudioIds: EMPTY_AUDIO_IDS,
      allAudio: EMPTY_AUDIO,
      allAudioIdUsesByAudioId: EMPTY_AUDIO_ID_USES_BY_AUDIO_ID,
    },
  };
  if (childrenArrayFieldName) { // if there is a childrenArrayFieldName, then the optimized version MUST have childrenArrayFieldName and childrenByIdFieldName set
    pureFunctionalUpdatedChildren ??= (ret as any)[childrenArrayFieldName] ?? [];
    (ret as any)[childrenArrayFieldName] = pureFunctionalUpdatedChildren;

    const optimizedChildrenByIdBefore: Record<string, ScanPartOptimized> | undefined = optimizedScanPartBefore
      ? (optimizedScanPartBefore as any)[childrenByIdFieldName ?? '']
      : undefined;

    // keep the old object if no change, otherwise create a new one
    (ret as any)[childrenByIdFieldName] = pureFunctionalUpdatedChildren === scanPartOptimizedChildrenBefore && optimizedChildrenByIdBefore
      ? optimizedChildrenByIdBefore
      : object(pureFunctionalUpdatedChildren, { withKey: child => child.id }); // e,g, lineItemsById, optionsById, tasksById, groupsById
  }

  // compute all the aggregate/reduced values
  ret.optimized.allPhotos = getAllPhotos(ret, pureFunctionalUpdatedChildren, optimizedScanPartBefore, scanPartOptimizedChildrenBefore);
  ret.optimized.allPhotoIds = getAllPhotoIdsFromAllPhotos(
    ret.optimized.allPhotos,
    optimizedScanPartBefore?.optimized.allPhotos,
    optimizedScanPartBefore?.optimized.allPhotoIds,
  );
  ret.optimized.allPhotoIdUsesByPhotoId = getAllPhotoIdUsesByPhotoId(
    ret,
    idsByDepth,
    pureFunctionalUpdatedChildren,
    optimizedScanPartBefore,
    scanPartOptimizedChildrenBefore,
  );
  ret.optimized.allVideos = getAllVideos(ret, pureFunctionalUpdatedChildren, optimizedScanPartBefore, scanPartOptimizedChildrenBefore);
  ret.optimized.allVideoIds = getAllVideoIdsFromAllVideos(
    ret.optimized.allVideos,
    optimizedScanPartBefore?.optimized.allVideos,
    optimizedScanPartBefore?.optimized.allVideoIds,
  );
  ret.optimized.allAudio = getAllAudio(ret, pureFunctionalUpdatedChildren, optimizedScanPartBefore, scanPartOptimizedChildrenBefore);
  ret.optimized.allAudioIds = getAllAudioIdsFromAllAudio(
    ret.optimized.allAudio,
    optimizedScanPartBefore?.optimized.allAudio,
    optimizedScanPartBefore?.optimized.allAudioIds,
  );
  ret.optimized.allAudioIdUsesByAudioId = getAllAudioIdUsesByAudioId(
    ret,
    idsByDepth,
    pureFunctionalUpdatedChildren,
    optimizedScanPartBefore,
    scanPartOptimizedChildrenBefore,
  );
  ret.optimized.reducedValues = getReducedValues(ret, pureFunctionalUpdatedChildren, optimizedScanPartBefore, scanPartOptimizedChildrenBefore);
  ret.optimized.searchText = getOptimizedSearchText(ret, pureFunctionalUpdatedChildren, optimizedScanPartBefore, scanPartOptimizedChildrenBefore);
  ret.scanStatus = getScanPartStatus(ret);

  // return the optimized scan part
  return ret;
};

// **************************************************************
// Get Optimized Scan
// **************************************************************
export const getOptimizedScan = (scanPart: ScanRoot, template: ScanTemplateRoot, optimizedScanPartBefore?: ScanRootOptimized): ScanRootOptimized =>
  getOptimizedScanPart(template, template, scanPart, SCAN_PART_DEPTH.scan, getIdsByDepth(), EMPTY_SCOPE, optimizedScanPartBefore) as ScanRootOptimized;

export const getOptimizedGroup = (
  scanRootTemplate: ScanTemplateRoot,
  group: ScanGroup,
  groupTemplate: ScanTemplateGroup,
  parentScope: Scope,
  optimizedScanPartBefore?: ScanGroupOptimized,
): ScanGroupOptimized =>
  getOptimizedScanPart(
    scanRootTemplate,
    groupTemplate,
    group,
    SCAN_PART_DEPTH.group,
    getIdsByDepth(group.id),
    parentScope,
    optimizedScanPartBefore,
  ) as ScanGroupOptimized;

export const getOptimizedTask = (
  task: ScanTask,
  taskTemplate: ScanTemplateTask,
  parentScope: Scope,
  optimizedScanPartBefore?: ScanTaskOptimized,
): ScanTaskOptimized =>
  getOptimizedScanPart(
    undefined as any,
    taskTemplate,
    task,
    SCAN_PART_DEPTH.task,
    getIdsByDepth(undefined, task.id),
    parentScope,
    optimizedScanPartBefore,
  ) as ScanTaskOptimized;

export const getOptimizedOption = (
  option: ScanOption,
  optionTemplate: ScanTemplateOption,
  parentScope: Scope,
  optimizedScanPartBefore?: ScanOptionOptimized,
): ScanOptionOptimized =>
  getOptimizedScanPart(
    undefined as any,
    optionTemplate,
    option,
    SCAN_PART_DEPTH.option,
    getIdsByDepth(undefined, undefined, option.id),
    parentScope,
    optimizedScanPartBefore,
  ) as ScanOptionOptimized;

export const getOptimizedLineItem = (
  lineItem: ScanLineItem,
  lineItemTemplate: ScanTemplateLineItem,
  parentScope: Scope,
  optimizedScanPartBefore?: ScanLineItemOptimized,
): ScanLineItemOptimized =>
  getOptimizedScanPart(
    undefined as any,
    lineItemTemplate,
    lineItem,
    SCAN_PART_DEPTH.lineItem,
    getIdsByDepth(undefined, undefined, undefined, lineItem.id),
    parentScope,
    optimizedScanPartBefore,
  ) as ScanLineItemOptimized;
